import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { AccountService } from "@/_services/account.service";
import { AssessmentService } from "@/_services/assessment.service";
import { IssueService } from "@/_services/issue.service";
import { PromotionService } from "@/_services/promotion.service";
import { filter, switchMap, tap, map } from "rxjs/operators";
import { SafetyAssuranceFeature } from "@features/SafetyAssuranceFeature";
import { SafetyPromotionFeature } from "@features/SafetyPromotionFeature";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { UserService } from "@/_services";
import { OnboardingGuideService } from "@/_services/onboarding-guide.service";
import { DismissedBanner } from "@/_models";
import { buildNewReportUrl } from "@/_utility/report-routing.utils";
import { AuthenticationService } from "@/_services/authentication.service";

interface QuickStartTile {
    icon: string;
    title: string;
    body: string;
    onClick: () => void;
}

@Component({
    selector: "app-quick-start-banner",
    standalone: true,
    imports: [CommonModule, SharedComponentsModule],
    templateUrl: "./quick-start-banner.component.html",
    styleUrl: "./quick-start-banner.component.scss",
})
export class QuickStartBannerComponent implements OnInit {
    @Input() isAdminOrOwner = false;
    @Output() hidden = new EventEmitter<void>();

    quickStartElements: QuickStartTile[] = [];

    constructor(
        private router: Router,
        private accountService: AccountService,
        private assessmentService: AssessmentService,
        private issueService: IssueService,
        private promoService: PromotionService,
        private userService: UserService,
        private onboardingGuideService: OnboardingGuideService,
        private authenticationService: AuthenticationService,
    ) {}

    ngOnInit() {
        const base: QuickStartTile[] = [
            {
                icon: "assets/icons/icon-clipboard-check.svg",
                title: "Risk Assessment",
                body: "Try submitting a risk assessment using our default risk form.",
                onClick: () => this.submitAssessment(),
            },
            {
                icon: "assets/icons/icon-dangerous-zone-sign.svg",
                title: "Hazard Report",
                body: "Try submitting a test hazard or incident report for review.",
                onClick: () => this.submitHazardReport(),
            },
        ];

        const asapReport: QuickStartTile = {
            icon: "assets/icons/icon-multiple-file.svg",
            title: "ASAP Report",
            body: "Submit an ASAP report quickly and securely, with automatic routing to authorities.",
            onClick: () => this.submitIncidentReport(),
        };

        const general: QuickStartTile[] =
            this.authenticationService.isAviationAccount()
                ? [...base, asapReport]
                : base;

        const admin: QuickStartTile[] = [
            ...base,
            {
                icon: "assets/icons/icon-task-list.svg",
                title: "Case Analysis",
                body: "Fill out a root cause analysis and create a corrective action plan.",
                onClick: () => this.startCorrectiveAction(),
            },
            {
                icon: "assets/icons/icon-annoncement-megaphone.svg",
                title: "Safety Promotion",
                body: "Send a safety notice to the team and track their acknowledgment.",
                onClick: () => this.sendPromotion(),
            },
            {
                icon: "assets/icons/icon-multiple-file.svg",
                title: "Report Templates",
                body: "Clone and customize starter form templates from our form library.",
                onClick: () => this.openTemplates(),
            },
        ];

        this.quickStartElements = this.isAdminOrOwner ? admin : general;
    }

    dismiss() {
        this.userService
            .dismissBanner(DismissedBanner.QuickStartHomeSection, true)
            .subscribe(() => this.hidden.emit());
    }

    private submitAssessment() {
        this.assessmentService
            .listAssessments()
            .pipe(
                map(
                    (assessments) =>
                        assessments.find((asmt) => asmt.isDefault) ||
                        assessments[0],
                ),
                tap((assessment) => {
                    if (assessment?._id) {
                        this.router.navigateByUrl(
                            buildNewReportUrl({
                                assessmentId: assessment._id,
                            }),
                        );
                    } else {
                        this.router.navigateByUrl(
                            buildNewReportUrl({
                                formFilter: "assessments",
                            }),
                        );
                    }
                }),
            )
            .subscribe();
    }

    private submitHazardReport() {
        this.assessmentService
            .getAssessmentsByType("hazard")
            .pipe(
                map(
                    (assessments) =>
                        assessments.find((asmt) => asmt.isDefault) ||
                        assessments[0],
                ),
                tap((assessment) => {
                    if (assessment?._id) {
                        this.router.navigateByUrl(
                            buildNewReportUrl({
                                assessmentId: assessment._id,
                            }),
                        );
                    } else {
                        this.router.navigateByUrl(
                            buildNewReportUrl({ formFilter: "hazards" }),
                        );
                    }
                }),
            )
            .subscribe();
    }

    private submitIncidentReport() {
        const asapFormId = this.onboardingGuideService.getAsapFormIdSnapshot();

        if (asapFormId) {
            this.router.navigateByUrl(
                buildNewReportUrl({ assessmentId: asapFormId }),
            );
        } else {
            this.assessmentService
                .getAssessmentsByType("incident")
                .pipe(
                    map(
                        (assessments) =>
                            assessments.find((asmt) => asmt.isDefault) ||
                            assessments[0],
                    ),
                    tap((assessment) => {
                        if (assessment?._id) {
                            this.router.navigateByUrl(
                                buildNewReportUrl({
                                    assessmentId: assessment._id,
                                }),
                            );
                        } else {
                            this.router.navigateByUrl(
                                buildNewReportUrl({
                                    formFilter: "incidents",
                                }),
                            );
                        }
                    }),
                )
                .subscribe();
        }
    }

    private startCorrectiveAction() {
        this.accountService
            .checkFormFeature(SafetyAssuranceFeature.key)
            .pipe(
                filter((result) => result),
                switchMap(() => this.issueService.createIssue()),
                tap((issue) => {
                    this.router.navigateByUrl(
                        `/assurance/registry/${issue._id}`,
                    );
                }),
            )
            .subscribe();
    }

    private sendPromotion() {
        this.accountService
            .checkFormFeature(SafetyPromotionFeature.key)
            .pipe(
                filter((result) => result),
                switchMap(() => this.promoService.createPromotion()),
                tap((promotion) => {
                    this.router.navigateByUrl(`/promotions/${promotion._id}`);
                }),
            )
            .subscribe();
    }

    private openTemplates() {
        this.router.navigateByUrl(`/assessments/new`);
    }
}
